<template>
    <VForm
        v-slot="{ errors }"
        @submit="onSubmit">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BaseInput
                v-model="model.firstname"
                :label="$t('auth.firstname')"
                rules="required|max:255"
                required
                autocomplete="given-name"
                name="register-firstname" />
            <BaseInput
                v-model="model.lastname"
                :label="$t('auth.lastname')"
                rules="required|max:255"
                required
                autocomplete="family-name"
                name="register-lastname" />
            <BaseInput
                v-model="model.email"
                :label="$t('auth.email')"
                class="md:col-span-2"
                rules="required|email|max:255"
                required
                autocomplete="username"
                type="email"
                name="register-email" />
            <BaseInput
                v-model="model.phone"
                :label="$t('auth.phone')"
                class="md:col-span-2"
                rules="required|min:5|max:30"
                required
                autocomplete="phone-number"
                name="register-phone" />
            <div>
                <BaseInput
                    v-model="model.password"
                    :label="$t('auth.password')"
                    type="password"
                    name="register-password"
                    rules="required|verify_password"
                    autocomplete="new-password"
                    required />
                <PasswordStrengthMeter
                    :model-value="model.password"
                    :strength-meter-only="true" />
            </div>

            <BaseInput
                v-model="model.password_confirmation"
                :label="$t('auth.repeatPassword')"
                :rules="{required: true, confirm_password: model.password}"
                required
                type="password"
                autocomplete="new-password"
                name="reset-password-confirmation" />

            <div class="md:col-span-2">
                <h3>{{ $t('auth.organisation') }}</h3>
                <div class="font-light text-sm">
                    {{ $t('auth.organisationHelpText') }}
                </div>
                <AuthCodeInput
                    v-model="model.authCode"
                    class="mt-4" />
            </div>

            <BaseAlert class="md:col-span-2">
                <div class="font-light text-sm">
                    {{ $t('auth.associationContact') }}
                    <div class="flex flex-col gap-y-4 mt-4">
                        <BaseSelect
                            v-model="memberOrganisationId"
                            :label="$t('auth.memberOrganisation')"
                            display-key="name"
                            :search-placeholder="$t('auth.memberOrganisation')"
                            nullable
                            api-route="spa.auth.memberOrganisations"
                            fetch-on-search
                            @update:entire-object="setMemberOrganisationContact($event)">
                            <template #option="option">
                                {{ option.name }}
                                <br>
                                <span class="flex gap-x-2">
                                <small class="opacity-75">{{ option.zipcode }} {{ option.city }}</small>
                            </span>
                            </template>
                        </BaseSelect>

                        <div v-if="memberOrganisationId">
                            <div v-if="memberOrganisationContact || memberOrganisationSecondContact">
                                <div
                                    v-if="memberOrganisationContact"
                                    class="flex flex-col gap-y-1 mt-2 mb-4">
                                    <span>
                                        {{ memberOrganisationContact.contact_person_firstname }}
                                        {{ memberOrganisationContact.contact_person_lastname }}
                                    </span>
                                    <div v-if="memberOrganisationContact.contact_person_phone">
                                        <span class="font-normal">Tel: </span>
                                        <a
                                            :href="`tel:${memberOrganisationContact.contact_person_phone}`"
                                            class="text-primary hover:underline">
                                            {{ memberOrganisationContact.contact_person_phone }}
                                        </a>
                                    </div>
                                    <div v-if="memberOrganisationContact.contact_person_email">
                                        <span class="font-normal">E-Mail: </span>
                                        <a
                                            :href="`mailto:${memberOrganisationContact.contact_person_email}`"
                                            class="text-primary hover:underline">
                                            {{ memberOrganisationContact.contact_person_email }}
                                        </a>
                                    </div>
                                </div>
                                <div
                                    v-if="memberOrganisationSecondContact"
                                    class="flex flex-col gap-y-1 mt-2 mb-4">
                                    <span>
                                        {{ memberOrganisationSecondContact.contact_person_firstname }}
                                        {{ memberOrganisationSecondContact.contact_person_lastname }}
                                    </span>
                                    <div v-if="memberOrganisationSecondContact.contact_person_phone">
                                        <span class="font-normal">Tel: </span>
                                        <a
                                            :href="`tel:${memberOrganisationSecondContact.contact_person_phone}`"
                                            class="text-primary hover:underline">
                                            {{ memberOrganisationSecondContact.contact_person_phone }}
                                        </a>
                                    </div>
                                    <div v-if="memberOrganisationSecondContact.contact_person_email">
                                        <span class="font-normal">E-Mail: </span>
                                        <a
                                            :href="`mailto:${memberOrganisationSecondContact.contact_person_email}`"
                                            class="text-primary hover:underline">
                                            {{ memberOrganisationSecondContact.contact_person_email }}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <span v-else>
                            {{ $t('auth.memberOrganisationNoContactPerson') }}
                        </span>
                        </div>
                    </div>

                    <div class="flex flex-col gap-y-1 mt-6">
                        <span class="font-semibold">{{ $t('auth.associationContactLabel') }}:</span>
                        <div v-if="aejContactPerson || aejSecondContactPerson">
                            <div
                                v-if="aejContactPerson"
                                class="flex flex-col gap-y-1 mt-2 mb-4">
                                <span>
                                    {{ aejContactPerson.contact_person_firstname }}
                                    {{ aejContactPerson.contact_person_lastname }}
                                </span>
                                <div v-if="aejContactPerson.contact_person_phone">
                                    <span class="font-normal">Tel: </span>
                                    <a
                                        :href="`tel:${aejContactPerson.contact_person_phone}`"
                                        class="text-primary hover:underline">
                                        {{ aejContactPerson.contact_person_phone }}
                                    </a>
                                </div>
                                <div v-if="aejContactPerson.contact_person_email">
                                    <span class="font-normal">E-Mail: </span>
                                    <a
                                        :href="`mailto:${aejContactPerson.contact_person_email}`"
                                        class="text-primary hover:underline">
                                        {{ aejContactPerson.contact_person_email }}
                                    </a>
                                </div>
                            </div>
                            <div
                                v-if="aejSecondContactPerson"
                                class="flex flex-col gap-y-1 mt-2 mb-4">
                                <span>
                                    {{ aejSecondContactPerson.contact_person_firstname }}
                                    {{ aejSecondContactPerson.contact_person_lastname }}
                                </span>
                                <div v-if="aejSecondContactPerson.contact_person_phone">
                                    <span class="font-normal">Tel: </span>
                                    <a
                                        :href="`tel:${aejSecondContactPerson.contact_person_phone}`"
                                        class="text-primary hover:underline">
                                        {{ aejSecondContactPerson.contact_person_phone }}
                                    </a>
                                </div>
                                <div v-if="aejSecondContactPerson.contact_person_email">
                                    <span class="font-normal">E-Mail: </span>
                                    <a
                                        :href="`mailto:${aejSecondContactPerson.contact_person_email}`"
                                        class="text-primary hover:underline">
                                        {{ aejSecondContactPerson.contact_person_email }}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <span v-else>{{ $t('auth.associationNoContactPerson') }}</span>
                    </div>
                </div>
            </BaseAlert>

            <BaseCheckbox
                v-model="model.privacyNoticeAcknowledged"
                class="md:col-span-2"
                :rules="privacyNoticeRule"
                name="register-privacy-notice">
                {{ $t('auth.privacyNoticeAcknowledged') }}
                <a
                    href="/datenverarbeitung"
                    target="_blank"
                    class="text-primary-600 hover:text-primary-700 underline">
                    {{ $t('auth.privacyNoticeLink') }}
                </a>
            </BaseCheckbox>

            <BaseButton
                class="w-full md:col-span-2"
                color="primary"
                :disabled="loading || !!Object.keys(errors).length"
                type="submit">
                <span v-if="!loading">{{ $t('auth.register') }}</span>
                <span v-else>
                    <LoadingSpinner />
                    {{ $t('auth.pleaseWait') }}
                </span>
            </BaseButton>

            <div class="flex justify-end md:col-span-2">
                <span
                    class="font-bold text-decoration-none cursor-pointer"
                    @click.prevent="emits('authPageVisible', 'AuthLogin')">{{ $t('auth.back') }}</span>
            </div>
        </div>
    </VForm>
</template>

<script setup>
import {useAuthStore} from '@/store/auth'

const {t: $t} = useI18n()
const authStore = useAuthStore()
const notification = useNotification()
const {$apiRoute} = useNuxtApp()

const aejContactPerson = ref(null)
const aejSecondContactPerson = ref(null)
const memberOrganisationId = ref(null)
const memberOrganisationContact = ref(null)
const memberOrganisationSecondContact = ref(null)

const model = ref({
    firstname: null,
    lastname: null,
    email: null,
    phone: null,
    password: null,
    password_confirmation: null,
    remember: true,
    authCode: null,
    privacyNoticeAcknowledged: false,
})

const emits = defineEmits(['authPageVisible'])

const loading = ref(false)

const privacyNoticeRule = value => value === true || $t('auth.privacyNoticeRequired')

async function onSubmit(values, actions) {
    loading.value = true

    await $lara.post($apiRoute('spa.auth.register'), {
        body: {...model.value}
    }).then(async response => {
        loading.value = false
        await login()
        notification.success($t('users.welcome', {name: model.value.firstname}))
    }).catch(async error => {
        loading.value = false
        notification.error(error?.response?._data?.message)
        // actions.setErrors(error.response._data?.errors);
    })
}


async function login() {
    loading.value = true

    await $lara.post($apiRoute('spa.login.authenticate'), {
        body: {...model.value}
    }).then(async response => {
        await authStore.successfulLogin('/', {celebrate: 1})
        loading.value = false
    }).catch(async error => {
        loading.value = false

        notification.error(error.response._data?.message)
    })
}

function getAEJContact() {
    $lara.get($apiRoute('spa.auth.aejContact')).then(response => {
        if (response.contact_person_firstname || response.contact_person_lastname || response.contact_person_email || response.contact_person_phone) {
            aejContactPerson.value = {
                contact_person_firstname: response.contact_person_firstname,
                contact_person_lastname: response.contact_person_lastname,
                contact_person_email: response.contact_person_email,
                contact_person_phone: response.contact_person_phone,
            }
        }
        if (response.second_contact_person_firstname || response.second_contact_person_lastname || response.second_contact_person_email || response.second_contact_person_phone) {
            aejSecondContactPerson.value = {
                contact_person_firstname: response.second_contact_person_firstname,
                contact_person_lastname: response.second_contact_person_lastname,
                contact_person_email: response.second_contact_person_email,
                contact_person_phone: response.second_contact_person_phone,
            }
        }
    }).catch(error => {
        console.error('Error fetching AEJ contact person:', error)
    })
}

function setMemberOrganisationContact(contact) {
    if (contact.contact_person_firstname || contact.contact_person_lastname || contact.contact_person_email || contact.contact_person_phone) {
        memberOrganisationContact.value = {
            contact_person_firstname: contact.contact_person_firstname,
            contact_person_lastname: contact.contact_person_lastname,
            contact_person_email: contact.contact_person_email,
            contact_person_phone: contact.contact_person_phone,
        }
    } else {
        memberOrganisationContact.value = null
    }
    if (contact.second_contact_person_firstname || contact.second_contact_person_lastname || contact.second_contact_person_email || contact.second_contact_person_phone) {
        memberOrganisationSecondContact.value = {
            contact_person_firstname: contact.second_contact_person_firstname,
            contact_person_lastname: contact.second_contact_person_lastname,
            contact_person_email: contact.second_contact_person_email,
            contact_person_phone: contact.second_contact_person_phone,
        }
    } else {
        memberOrganisationSecondContact.value = null
    }
}

onMounted(() => {
    getAEJContact()
})
</script>